import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WishlistContext = createContext();

// Generate or retrieve session ID for wishlist
const getSessionId = () => {
  let sessionId = localStorage.getItem('wishlist_session_id');
  if (!sessionId) {
    sessionId = 'wl_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('wishlist_session_id', sessionId);
  }
  return sessionId;
};

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const sessionId = getSessionId();

  const fetchWishlist = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/wishlist?session_id=${sessionId}`);
      setWishlistItems(res.data.items || []);
      setWishlistCount(res.data.count || 0);
    } catch (e) {
      console.error('Error fetching wishlist:', e);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const addToWishlist = async (productId, productName) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/wishlist/add?session_id=${sessionId}`, {
        product_id: productId
      });
      setWishlistCount(res.data.count);
      await fetchWishlist();
      toast.success(`${productName || 'Product'} added to wishlist!`);
      return true;
    } catch (e) {
      if (e.response?.data?.detail === 'Product already in wishlist') {
        toast.info('Product already in your wishlist');
      } else {
        toast.error('Failed to add to wishlist');
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId, productName) => {
    setLoading(true);
    try {
      const res = await axios.delete(`${API_URL}/api/wishlist/remove/${productId}?session_id=${sessionId}`);
      setWishlistCount(res.data.count);
      await fetchWishlist();
      toast.success(`${productName || 'Product'} removed from wishlist`);
      return true;
    } catch (e) {
      toast.error('Failed to remove from wishlist');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const isInWishlist = (productId) => {
    return wishlistItems.some(item => item.product_id === productId);
  };

  const clearWishlist = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/wishlist/clear?session_id=${sessionId}`);
      setWishlistItems([]);
      setWishlistCount(0);
      toast.success('Wishlist cleared');
    } catch (e) {
      toast.error('Failed to clear wishlist');
    } finally {
      setLoading(false);
    }
  };

  return (
    <WishlistContext.Provider value={{
      wishlistItems,
      wishlistCount,
      loading,
      addToWishlist,
      removeFromWishlist,
      isInWishlist,
      clearWishlist,
      fetchWishlist
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
